import React, { useState, useEffect } from "react";
import { Carousel, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import ProductQuickView from "./shop/ProductQuickView";
import { useCart } from "@/contexts/CartContext";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import ProductCard from "./shop/ProductCard";
import { Product } from "@/types/product";
import { useLanguage } from "@/contexts/LanguageContext";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const defaultProductImage = "/lovable-uploads/60244da3-6deb-4628-b29a-c8e90866808e.png";

interface DatabaseProduct {
  id: string;
  name: string;
  image: string;
  points_price: number;
  description: string | null;
  stock: number;
  category_id: string | null;
  additional_images?: string[];
  brand_id?: string | null;
  price?: number;
}

const FeaturedProducts = () => {
  const { t } = useLanguage();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);
  const { addToCart } = useCart();
  const { toast } = useToast();
  const { ref, isVisible } = useScrollAnimation();

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('products')
          .select(`
            *,
            brands:brand_id (name, logo)
          `)
          .order('created_at', { ascending: false })
          .limit(4);
        
        if (error) throw error;
        
        // Convert database format to Product interface
        const convertedProducts: Product[] = (data || []).map((dbProduct: DatabaseProduct) => ({
          id: dbProduct.id,
          name: dbProduct.name,
          description: dbProduct.description || '',
          image: dbProduct.image || defaultProductImage,
          price: dbProduct.price || 0,
          pointsPrice: dbProduct.points_price || 0,
          brandId: dbProduct.brand_id || '',
          categoryId: dbProduct.category_id || '',
          stock: dbProduct.stock || 10,
          additionalImages: dbProduct.additional_images
        }));
        
        setProducts(convertedProducts);
      } catch (error) {
        console.error("Error loading featured products:", error instanceof Error ? error.message : 'Unknown error');
        toast({
          title: t('products.loadError'),
          description: t('products.loadError'),
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedProducts();
  }, [toast, t]);

  const handleQuickView = (product: Product) => {
    setSelectedProduct(product);
    setIsQuickViewOpen(true);
  };

  const handleCloseQuickView = () => {
    setIsQuickViewOpen(false);
  };

  const handleAddToCart = (product: Product) => {
    addToCart(product, 1);
    // Toast is now handled in CartContext
  };

  // Se estiver carregando ou sem produtos, mostrar placeholders temporários
  const displayProducts = loading || products.length === 0 ? [
    {
      id: "1",
      name: "Gaming Headset 1",
      description: "High-quality gaming headset with noise cancellation",
      image: "lovable-uploads/Mask-Group-5-3.png",
      price: 0,
      pointsPrice: 150,
      brandId: "",
      categoryId: "",
      stock: 10
    },
    {
      id: "2",
      name: "Gaming Headset 2",
      description: "Wireless gaming headset with long battery life",
      image: "lovable-uploads/Mask-Group-5-3.png",
      price: 0,
      pointsPrice: 200,
      brandId: "",
      categoryId: "",
      stock: 15
    },
    {
      id: "3",
      name: "Gaming Headset 3",
      description: "RGB gaming headset with customizable lights",
      image: "lovable-uploads/Mask-Group-5-3.png",
      price: 0,
      pointsPrice: 250,
      brandId: "",
      categoryId: "",
      stock: 5
    },
    {
      id: "4",
      name: "Gaming Headset 4",
      description: "Professional gaming headset for esports",
      image: "lovable-uploads/headset-470x353.jpg",
      price: 0,
      pointsPrice: 300,
      brandId: "",
      categoryId: "",
      stock: 20
    },
  ] : products;

  return (
    <section ref={ref as React.RefObject<HTMLElement>} className="py-16 bg-brand-darker">
      <div className="container mx-auto px-4">
        <h2 className={`section-heading transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>{t('home.mainProducts')}</h2>
        <p className={`section-subheading transition-all duration-1000 delay-150 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>{t('home.bestSellers')}</p>
        
        <Carousel
          className={`my-8 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
          opts={{
            align: "start",
            loop: true,
          }}
        >
          <CarouselContent className="-ml-2 md:-ml-4">
            {displayProducts.map((product, index) => (
              <CarouselItem key={product.id} className="basis-1/2 md:basis-1/2 lg:basis-1/3 xl:basis-1/4 pl-2 md:pl-4">
                <div className="md:hover-lift pr-2 md:pr-0">
                  <ProductCard
                    product={product}
                    onQuickView={() => handleQuickView(product)}
                  />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        <ProductQuickView 
          product={selectedProduct}
          isOpen={isQuickViewOpen}
          onClose={handleCloseQuickView}
          onAddToCart={handleAddToCart}
        />
      </div>
    </section>
  );
};

export default FeaturedProducts;
